// src/pages/Exams/ExamRegistrationList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Pager from '../../components/common/Pager';
import { supabase } from '../../supabaseClient';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useExamRegistrationsPage } from '../../hooks/useExamRegistrationsPage';

const STATUS_OPTIONS = ['All', 'Registered', 'Deferred', 'Cancelled'];

function badgeClass(status) {
  switch (status) {
    case 'Registered':
      return 'bg-green-100 text-green-800';
    case 'Deferred':
      return 'bg-yellow-100 text-yellow-800';
    case 'Cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export default function ExamRegistrationList() {
  // Filters
  const [sessions, setSessions] = useState([]); // dropdown
  const [sessionFilter, setSessionFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  // Debounce search to avoid refetch on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Pagination (server-side)
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Load sessions for dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('exam_sessions')
          .select('id, name, start_date')
          .order('start_date', { ascending: false })
          .order('name', { ascending: true });
        if (error) throw error;
        if (!cancelled) setSessions(data || []);
      } catch (e) {
        console.error('Failed to load exam_sessions', e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Map UI filters to hook params
  const statusParam = statusFilter === 'All' ? 'all' : statusFilter;
  const sessionParam = sessionFilter === 'All' ? 'all' : String(sessionFilter);

  // 🔄 Auto-refresh triggers
  const [refreshKey, setRefreshKey] = useState(0);

  // 1) Immediate refresh via Supabase Realtime (insert/update/delete)
  useEffect(() => {
    // Debounce rapid bursts a bit to avoid refetch storms
    let debounceTimer = null;
    const bump = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setRefreshKey((k) => k + 1);
      }, 120);
    };

    const channel = supabase
      .channel('exam-registrations-list')
      // parent table
      .on(
        'postgres_changes',
        { event: '*', schema: 'rism', table: 'student_exam_session' },
        bump
      )
      // child table (subject rows)
      .on(
        'postgres_changes',
        { event: '*', schema: 'rism', table: 'student_exam_info' },
        bump
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  // 2) Fallback: refresh when page regains focus / is shown from bfcache / becomes visible
  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    const onVis = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('focus', bump);
    window.addEventListener('pageshow', bump);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', bump);
      window.removeEventListener('pageshow', bump);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Server-side data (rows + count)
  const {
    rows,
    count,
    isLoading,
    error,
    softDeleteRegistration,
  } = useExamRegistrationsPage({
    // ⬇️ Changing this key forces the hook to refetch while keeping your exact logic intact
    refreshKey,
    page: currentPage,
    pageSize,
    search: debouncedSearch,      // debounced
    status: statusParam,          // 'all' | 'Registered' | 'Deferred' | 'Cancelled'
    sessionId: sessionParam,      // 'all' | UUID
  });

  // Reset to page 1 when filters change (using debounced search)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusParam, sessionParam, pageSize]);

  // Clamp page if total shrinks
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((count || 0) / (pageSize || 1)));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [count, pageSize, currentPage]);

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('Soft-delete this exam registration? This will also hide all its subjects.')) return;
    try {
      await softDeleteRegistration(id);
      alert('Registration deleted.');
      // proactively refresh the list after delete
      setRefreshKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Delete failed.');
    }
  };

  const title = useMemo(() => 'Exam Registrations Management', []);
  const isTyping = search.trim() !== debouncedSearch;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        <Link to="/exam-registrations/new">
          <Button>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add Exam Registration
          </Button>
        </Link>
      </div>

      {/* Error banner (non-blocking; never unmounts filters) */}
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <span className="text-xs text-gray-500">
                {isTyping ? 'Typing…' : isLoading ? 'Searching…' : ''}
              </span>
            </div>
            <input
              id="search"
              type="text"
              placeholder="Search by Student or Session…"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Session */}
          <div>
            <label htmlFor="session" className="block text-sm font-medium text-gray-700 mb-1">
              Exam Session
            </label>
            <select
              id="session"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
            >
              <option value="All">All Sessions</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Registration Status
            </label>
            <select
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === 'All' ? 'All Statuses' : s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {count === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {isLoading && !isTyping
              ? 'Loading exam registrations…'
              : `No registrations${
                  debouncedSearch || statusParam !== 'all' || sessionParam !== 'all'
                    ? ' match your filters'
                    : ' found'
                }`}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              {/* Subtle top pulse while fetching, without unmounting anything */}
              {isLoading && !isTyping && (
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent animate-pulse" />
              )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subjects</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{r.student_name || '—'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{r.exam_session_name || '—'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{r.subjects_total ?? 0}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-xs text-gray-900">
                          {(r.reg_registered ?? 0)}/{r.subjects_total ?? 0}
                          {((r.reg_deferred ?? 0) > 0 || (r.reg_cancelled ?? 0) > 0) && (
                            <span className="text-gray-500">
                              {' '}
                              (def:{r.reg_deferred ?? 0}, canc:{r.reg_cancelled ?? 0})
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-xs text-gray-900">
                          {(r.res_passed ?? 0)}/{r.subjects_total ?? 0}
                          {(r.res_pending ?? 0) > 0 && (
                            <span className="text-gray-500"> (pending:{r.res_pending ?? 0})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badgeClass(r.registration_status)}`}>
                          {r.registration_status}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/exam-registrations/${r.id}`}>
                            <Button variant="outline" size="sm">View</Button>
                          </Link>
                          <Link to={`/exam-registrations/${r.id}/edit`}>
                            <Button variant="outline" size="sm">Edit</Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(r.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer — shared Pager (server-side count) */}
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalRows={count}
            />
          </>
        )}
      </div>
    </div>
  );
}
